import { DomainCaseStatus } from './enums';

/**
 * A case's status as the client is told it, derived from the panel's status
 * every time it is read — never stored, so it cannot drift from what support
 * set. Hold is shown as Hold, and only as the word: why it was parked stays
 * support's.
 */
export const CustomerCaseStatus = {
  RECEIVED: 'received',
  IN_PROGRESS: 'in_progress',
  HOLD: 'hold',
  REMOVED: 'removed',
  UNABLE_TO_REMOVE: 'unable_to_remove',
  CANCELLED: 'cancelled',
} as const;

export type CustomerCaseStatus = (typeof CustomerCaseStatus)[keyof typeof CustomerCaseStatus];

const CUSTOMER_STATUS_OF: Readonly<Record<DomainCaseStatus, CustomerCaseStatus>> = {
  [DomainCaseStatus.PENDING]: CustomerCaseStatus.RECEIVED,
  [DomainCaseStatus.IN_PROGRESS]: CustomerCaseStatus.IN_PROGRESS,
  [DomainCaseStatus.HOLD]: CustomerCaseStatus.HOLD,
  [DomainCaseStatus.DECLINED]: CustomerCaseStatus.UNABLE_TO_REMOVE,
  [DomainCaseStatus.DONE]: CustomerCaseStatus.REMOVED,
  [DomainCaseStatus.CANCELLED]: CustomerCaseStatus.CANCELLED,
};

/** An unknown code reads as In progress rather than as a finished outcome. */
export function customerCaseStatus(status: DomainCaseStatus | string): CustomerCaseStatus {
  return CUSTOMER_STATUS_OF[status as DomainCaseStatus] ?? CustomerCaseStatus.IN_PROGRESS;
}

/** The statuses a client can filter by, in the order work moves. */
export const CUSTOMER_CASE_STATUSES: readonly CustomerCaseStatus[] = [
  CustomerCaseStatus.RECEIVED,
  CustomerCaseStatus.IN_PROGRESS,
  CustomerCaseStatus.HOLD,
  CustomerCaseStatus.REMOVED,
  CustomerCaseStatus.UNABLE_TO_REMOVE,
  CustomerCaseStatus.CANCELLED,
];

export function customerCaseIsOpen(status: DomainCaseStatus | string): boolean {
  const shown = customerCaseStatus(status);
  return (
    shown === CustomerCaseStatus.RECEIVED ||
    shown === CustomerCaseStatus.IN_PROGRESS ||
    shown === CustomerCaseStatus.HOLD
  );
}

/** The three stages the progress indicator draws. */
export const CUSTOMER_CASE_STAGES = [
  CustomerCaseStatus.RECEIVED,
  CustomerCaseStatus.IN_PROGRESS,
  CustomerCaseStatus.REMOVED,
] as const;

export interface CustomerCaseProgress {
  /** How many of {@link CUSTOMER_CASE_STAGES} are reached, 1–3. */
  reached: number;
  /**
   * `stopped` for Unable to remove and Cancelled: the track stops where work
   * stopped and is never drawn complete, because nothing was removed.
   * `paused` for Hold: still open, drawn neutral, never as complete.
   */
  outcome: 'active' | 'success' | 'stopped' | 'paused';
}

export function customerCaseProgress(status: DomainCaseStatus | string): CustomerCaseProgress {
  switch (customerCaseStatus(status)) {
    case CustomerCaseStatus.RECEIVED:
      return { reached: 1, outcome: 'active' };
    case CustomerCaseStatus.REMOVED:
      return { reached: 3, outcome: 'success' };
    case CustomerCaseStatus.UNABLE_TO_REMOVE:
      return { reached: 2, outcome: 'stopped' };
    case CustomerCaseStatus.CANCELLED:
      return { reached: 1, outcome: 'stopped' };
    case CustomerCaseStatus.HOLD:
      // Taken and parked, so past Received; the middle stage reads Hold.
      return { reached: 2, outcome: 'paused' };
    default:
      return { reached: 2, outcome: 'active' };
  }
}

/**
 * The four figures over the client's Removals list, counted per case except
 * Watching, which is per page because re-checks are per page.
 *
 * - `open`: Received, In progress or Hold.
 * - `awaitingOutcome`: the part of `open` where a notice has gone out.
 * - `removed`: Done. Unable to remove and Cancelled are counted nowhere.
 * - `watching`: distinct pages under the re-upload schedule (`goneAt` set),
 *   outside Cancelled cases.
 */
export interface ClientRemovalStats {
  open: number;
  awaitingOutcome: number;
  removed: number;
  watching: number;
}
